/**
 * Utility for cleaning up localStorage when switching between tenants/users
 * This ensures that previous user's preferences and settings don't interfere
 * with the new user's session
 */

// List of localStorage keys that should be preserved across tenant switches
const PRESERVED_KEYS = [
  'i18nextLng', // Language preference can be preserved
  'theme', // Theme preference can be preserved
];

// List of localStorage keys that are user/tenant-specific and should be cleared
const USER_SPECIFIC_KEYS = [
  // Authentication data
  'token',
  'user',
  'tenantId',
  
  // Tickets page preferences
  'ticketsSelectedStatus',
  'ticketsSearchQuery',
  'ticketsSortBy',
  'ticketsSortOrder',
  'ticketsCurrentPage',
  'ticketsViewMode',
  'ticketsSelectedTags',
  'ticketsRequestedBy',
  'ticketsAssignedTo',
  
  // Date range preferences (various pages)
  'ticketsDateRange',
  'reportsDateRange',
  'analyticsDateRange',
  'dashboardDateRange',
  
  // Dashboard preferences
  'dashboardLayout',
  'dashboardWidgets',
  'dashboardFilters',
  
  // Form and UI state
  'formDrafts',
  'sidebarCollapsed',
  'tableColumnWidths',
  'tableColumnVisibility',
  
  // Notification preferences
  'notificationSettings',
  'lastNotificationCheck',
  
  // Other user-specific data
  'userPreferences',
  'recentSearches',
  'favoriteTickets',
  'customFilters',
];

/**
 * Clear all user-specific localStorage data while preserving system preferences
 * @param preserveTenantId - Whether to preserve the tenantId (useful during login)
 */
export const clearUserSpecificLocalStorage = (preserveTenantId: boolean = false): void => {
  
  // Clear known user-specific keys
  USER_SPECIFIC_KEYS.forEach(key => {
    // Skip tenantId if we want to preserve it
    if (preserveTenantId && key === 'tenantId') {
      return;
    }
    
    if (localStorage.getItem(key) !== null) {
      localStorage.removeItem(key);
    }
  });
  
  // Also clear any keys that follow common patterns for user-specific data
  const allKeys = Object.keys(localStorage);
  const patternKeys = allKeys.filter(key => {
    // Clear keys that contain user ID patterns, ticket IDs, etc.
    return key.includes('user_') || 
           key.includes('ticket_') || 
           key.includes('filter_') ||
           key.includes('sort_') ||
           key.includes('view_') ||
           key.includes('search_') ||
           key.includes('draft_') ||
           key.match(/^\d+_/); // Keys starting with numbers (likely IDs)
  });
  
  patternKeys.forEach(key => {
    if (!PRESERVED_KEYS.includes(key)) {
      localStorage.removeItem(key);
    }
  });
};

/**
 * Clear all sessionStorage data (session data should always be cleared)
 */
export const clearSessionStorage = (): void => {
  sessionStorage.clear();
};

/**
 * Comprehensive cleanup for tenant/user switching
 * @param preserveTenantId - Whether to preserve the tenantId (useful during login)
 */
export const clearAllUserData = (preserveTenantId: boolean = false): void => {
  clearUserSpecificLocalStorage(preserveTenantId);
  clearSessionStorage();
};

/**
 * Cleanup for login - preserves tenantId but clears user preferences
 */
export const clearUserDataForLogin = (): void => {
  clearAllUserData(true); // Preserve tenantId during login
};

/**
 * Cleanup for logout - clears everything including tenantId
 */
export const clearUserDataForLogout = (): void => {
  clearAllUserData(false); // Clear everything including tenantId
};

/**
 * Get list of preserved keys (for debugging/testing)
 */
export const getPreservedKeys = (): string[] => {
  return [...PRESERVED_KEYS];
};

/**
 * Get list of user-specific keys (for debugging/testing)
 */
export const getUserSpecificKeys = (): string[] => {
  return [...USER_SPECIFIC_KEYS];
};
